import { useState, useEffect } from 'react';
import { Box, Button, ButtonGroup, TextField } from '@mui/material';
import { DateRange, Clear } from '@mui/icons-material';

type DatePreset = '24hours' | '7days' | '30days' | '90days' | 'custom';

interface JotCoachDateRangePickerProps {
  startDate?: string | null;
  endDate?: string | null;
  onChange: (startDate: string | null, endDate: string | null) => void;
  defaultPreset?: DatePreset;
}

export function JotCoachDateRangePicker({
  startDate: initialStartDate,
  endDate: initialEndDate,
  onChange,
  defaultPreset = '7days',
}: JotCoachDateRangePickerProps) {
  const [preset, setPreset] = useState<DatePreset>(defaultPreset);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');

  // Calculate date range based on preset
  const calculateDateRange = (selectedPreset: DatePreset): [string, string] | [null, null] => {
    if (selectedPreset === 'custom') {
      return [null, null];
    }

    // Use tomorrow as end date to ensure today is fully included
    // (API treats end_date as exclusive, so we want up to but not including tomorrow)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const endDate = tomorrow.toISOString().split('T')[0];

    let daysAgo: number;
    switch (selectedPreset) {
      case '24hours':
        daysAgo = 1;
        break;
      case '7days':
        daysAgo = 7;
        break;
      case '30days':
        daysAgo = 30;
        break;
      case '90days':
        daysAgo = 90;
        break;
      default:
        return [null, null];
    }

    const startDateObj = new Date();
    startDateObj.setDate(startDateObj.getDate() - daysAgo);
    const startDate = startDateObj.toISOString().split('T')[0];

    return [startDate, endDate];
  };

  // Initialize with default preset
  useEffect(() => {
    if (!initialStartDate && !initialEndDate) {
      const [start, end] = calculateDateRange(defaultPreset);
      if (start && end) {
        onChange(start, end);
      }
    }
  }, []);

  const handlePresetClick = (selectedPreset: DatePreset) => {
    setPreset(selectedPreset);

    if (selectedPreset === 'custom') {
      // Don't auto-apply custom dates, wait for user input
      return;
    }

    const [start, end] = calculateDateRange(selectedPreset);
    if (start && end) {
      onChange(start, end);
    }
  };

  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate) {
      onChange(customStartDate, customEndDate);
    }
  };

  const handleClear = () => {
    setPreset('7days');
    setCustomStartDate('');
    setCustomEndDate('');
    const [start, end] = calculateDateRange('7days');
    if (start && end) {
      onChange(start, end);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <DateRange color="action" />
        <ButtonGroup variant="outlined" size="small">
          <Button
            variant={preset === '24hours' ? 'contained' : 'outlined'}
            onClick={() => handlePresetClick('24hours')}
          >
            Last 24 hours
          </Button>
          <Button
            variant={preset === '7days' ? 'contained' : 'outlined'}
            onClick={() => handlePresetClick('7days')}
          >
            Last 7 days
          </Button>
          <Button
            variant={preset === '30days' ? 'contained' : 'outlined'}
            onClick={() => handlePresetClick('30days')}
          >
            Last 30 days
          </Button>
          <Button
            variant={preset === '90days' ? 'contained' : 'outlined'}
            onClick={() => handlePresetClick('90days')}
          >
            Last 90 days
          </Button>
          <Button
            variant={preset === 'custom' ? 'contained' : 'outlined'}
            onClick={() => handlePresetClick('custom')}
          >
            Custom
          </Button>
        </ButtonGroup>
        <Button
          size="small"
          startIcon={<Clear />}
          onClick={handleClear}
          disabled={preset === '7days' && !customStartDate && !customEndDate}
        >
          Clear
        </Button>
      </Box>

      {preset === 'custom' && (
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            type="date"
            label="Start Date"
            size="small"
            value={customStartDate}
            onChange={e => setCustomStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="End Date"
            size="small"
            value={customEndDate}
            onChange={e => setCustomEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleCustomDateChange}
            disabled={!customStartDate || !customEndDate}
          >
            Apply
          </Button>
        </Box>
      )}
    </Box>
  );
}
